import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Icons } from "@/components/icons";
import { toast } from "sonner";

export default function Settings() {
  const [settings, setSettings] = useState({
    // Profile Settings
    fullName: "Dr. Amina Al-Zahra",
    email: "amina.coordinator@taawun.qa",
    phone: "+974 5555 5678",
    region: "Greater Doha Area",
    bio: "Senior HAF coordinator with 8+ years experience in community fundraising coordination and team management.",

    // Notification Settings
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: true,
    dailyReports: true,
    approvalAlerts: true,
    collectorUpdates: true,

    // Coordination Settings
    autoApprovalLimit: 1000,
    requireSecondApproval: true,
    collectorLocationTracking: true,
    mandatoryReceiptGeneration: true,

    // Team Management
    maxCollectorsPerRegion: 15,
    collectorPerformanceTracking: true,
    automaticAssignments: false,
  });

  const handleSave = () => {
    // Simulate saving
    toast.success("Settings saved successfully!");
  };

  const handleInputChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">
          Coordinator Settings
        </h2>
        <p className="text-muted-foreground">
          Manage your coordination preferences and team management settings
        </p>
      </div>

      <div className="grid gap-6">
        {/* Profile Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>
              Update your personal information and coordination region
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  value={settings.fullName}
                  onChange={(e) =>
                    handleInputChange("fullName", e.target.value)
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={settings.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={settings.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="region">Coordination Region</Label>
                <Input
                  id="region"
                  value={settings.region}
                  onChange={(e) => handleInputChange("region", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                placeholder="Tell us about your coordination experience..."
                value={settings.bio}
                onChange={(e) => handleInputChange("bio", e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Notification Preferences</CardTitle>
            <CardDescription>
              Configure how you receive coordination alerts and updates
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive coordination updates via email
                </p>
              </div>
              <Switch
                checked={settings.emailNotifications}
                onCheckedChange={(checked) =>
                  handleInputChange("emailNotifications", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>SMS Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive urgent coordination alerts via SMS
                </p>
              </div>
              <Switch
                checked={settings.smsNotifications}
                onCheckedChange={(checked) =>
                  handleInputChange("smsNotifications", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Approval Alerts</Label>
                <p className="text-sm text-muted-foreground">
                  Immediate alerts for payments requiring approval
                </p>
              </div>
              <Switch
                checked={settings.approvalAlerts}
                onCheckedChange={(checked) =>
                  handleInputChange("approvalAlerts", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Collector Updates</Label>
                <p className="text-sm text-muted-foreground">
                  Notifications about collector activities and performance
                </p>
              </div>
              <Switch
                checked={settings.collectorUpdates}
                onCheckedChange={(checked) =>
                  handleInputChange("collectorUpdates", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Daily Reports</Label>
                <p className="text-sm text-muted-foreground">
                  Receive daily coordination summaries
                </p>
              </div>
              <Switch
                checked={settings.dailyReports}
                onCheckedChange={(checked) =>
                  handleInputChange("dailyReports", checked)
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Coordination Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Coordination Settings</CardTitle>
            <CardDescription>
              Configure approval workflows and coordination preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="autoApprovalLimit">
                  Auto-Approval Limit (QAR)
                </Label>
                <Input
                  id="autoApprovalLimit"
                  type="number"
                  value={settings.autoApprovalLimit}
                  onChange={(e) =>
                    handleInputChange(
                      "autoApprovalLimit",
                      parseInt(e.target.value)
                    )
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Payments below this amount are auto-approved
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxCollectorsPerRegion">
                  Max Collectors per Region
                </Label>
                <Input
                  id="maxCollectorsPerRegion"
                  type="number"
                  value={settings.maxCollectorsPerRegion}
                  onChange={(e) =>
                    handleInputChange(
                      "maxCollectorsPerRegion",
                      parseInt(e.target.value)
                    )
                  }
                />
              </div>
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Require Second Approval</Label>
                <p className="text-sm text-muted-foreground">
                  High-value payments require secondary approval
                </p>
              </div>
              <Switch
                checked={settings.requireSecondApproval}
                onCheckedChange={(checked) =>
                  handleInputChange("requireSecondApproval", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Collector Location Tracking</Label>
                <p className="text-sm text-muted-foreground">
                  Track collector locations for verification
                </p>
              </div>
              <Switch
                checked={settings.collectorLocationTracking}
                onCheckedChange={(checked) =>
                  handleInputChange("collectorLocationTracking", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Mandatory Receipt Generation</Label>
                <p className="text-sm text-muted-foreground">
                  All approved payments must generate receipts
                </p>
              </div>
              <Switch
                checked={settings.mandatoryReceiptGeneration}
                onCheckedChange={(checked) =>
                  handleInputChange("mandatoryReceiptGeneration", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Collector Performance Tracking</Label>
                <p className="text-sm text-muted-foreground">
                  Monitor and analyze collector performance metrics
                </p>
              </div>
              <Switch
                checked={settings.collectorPerformanceTracking}
                onCheckedChange={(checked) =>
                  handleInputChange("collectorPerformanceTracking", checked)
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Automatic Assignments</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically assign new collectors to regions
                </p>
              </div>
              <Switch
                checked={settings.automaticAssignments}
                onCheckedChange={(checked) =>
                  handleInputChange("automaticAssignments", checked)
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Team Management */}
        <Card>
          <CardHeader>
            <CardTitle>Team Management</CardTitle>
            <CardDescription>
              Quick actions for managing your collector team
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Button variant="outline" className="w-full">
                <Icons.users className="mr-2 h-4 w-4" />
                Manage Collectors
              </Button>

              <Button variant="outline" className="w-full">
                <Icons.search className="mr-2 h-4 w-4" />
                Performance Reports
              </Button>

              <Button variant="outline" className="w-full">
                <Icons.calendar className="mr-2 h-4 w-4" />
                Schedule Training
              </Button>

              <Button variant="outline" className="w-full">
                <Icons.fileText className="mr-2 h-4 w-4" />
                Export Team Data
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Security & Access</CardTitle>
            <CardDescription>
              Manage your account security and access permissions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" className="w-full">
              <Icons.key className="mr-2 h-4 w-4" />
              Change Password
            </Button>

            <Button variant="outline" className="w-full">
              <Icons.smartphone className="mr-2 h-4 w-4" />
              Setup Two-Factor Authentication
            </Button>

            <Button variant="outline" className="w-full">
              <Icons.download className="mr-2 h-4 w-4" />
              Download Account Data
            </Button>

            <Button variant="outline" className="w-full">
              <Icons.user className="mr-2 h-4 w-4" />
              Manage Access Permissions
            </Button>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} className="w-32">
            <Icons.save className="mr-2 h-4 w-4" />
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
